import {SupportedLocales} from '#types';

export const destinationsLang = {
  [SupportedLocales.EN]: {
    title: 'Destinations',
    subtitle: 'Manage destinations for your audiences.',
    table: {
      ariaLabel: 'Destinations table',
      loadingAriaLabel: 'Loading destinations',
      search: 'Search',
      type: 'Type',
      name: 'Name',
      activeAudiences: 'Active Audiences',
      syncFrequency: 'Sync Frequency',
      status: 'Status',
      lastUpdated: 'Last Updated',
      createdAt: 'Created At',
      options: 'Options',
      active: 'Active',
      emptyValue: '-',
      empty: 'There are no destinations.',
      emptyCaption: "Looks like you haven't added a destination.",
      emptySearch: 'There are no destinations matching your search.',
      connect: 'Connect Destination',
      deleteAriaLabel: (name: string) => `Delete ${name}`,
    },
    reauthenticate: {
      action: 'Re-authenticate',
      tooltip:
        'Destination authentication expired. Please re-authenticate to re-start your sync.',
    },
    delete: {
      title: 'Delete Destination',
      confirm: 'Confirm',
      cancel: 'Cancel',
      withActiveExports:
        'This destination has active exports. Deleting this destination will disable all future exports, but will keep record of your past exports to this destination.',
      confirmQuestion: 'Are you sure you want to delete this Destination?',
      success: 'Destination has been removed.',
      failed: 'Failed to delete destination',
    },
    oauth: {
      connectionError: 'Cannot connect to your destination at this time',
    },
    edit: {
      loadingAriaLabel: 'Loading destination',
      invalidId: 'This destination could not be found.',
      loadFailed: 'Destination could not be loaded.',
      platformLabel: 'Platform',
      headerCaption: 'Modify your destination platform.',
      breadcrumbLabel: 'Breadcrumb',
      replaceSavedCredential: (label: string) => `Replace saved ${label}`,
      destinationDetails: 'Destination',
      name: 'Destination name',
      destinationNamePlaceholder: 'Destination Name',
      syncFrequency: 'Sync frequency',
      selectSyncFrequency: 'Sync frequency',
      syncFrequencyOptions: {
        hourly: 'Hourly',
        daily: 'Daily',
      },
      enabledLabel: 'Enabled',
      channelConfiguration: 'Channel configuration',
      selectChannelConfiguration: 'Channel configuration',
      ingest: 'Ingest',
      importSource: {
        notIngesting: 'It looks like you are not ingesting data.',
        ingesting:
          'This is the source where you are ingesting data from this destination. You can select a different source if you want to change it.',
        selectExisting:
          'If you are already ingesting, please select the ingest source where you are getting this data.',
      },
      importSourceCaption: {
        withSources: {
          prefix:
            'You can also add a new ingest source by following this link: ',
          suffix: ', if none of the ones above is the one you are using.',
        },
        withoutSources: {
          prefix:
            'You can add a source to ingest this destination by following this link: ',
        },
      },
      addImportSource: 'Add Ingest Source',
      selectImportSource: 'Ingest Source',
      notIngestReason: 'Or tell us Why you are not ingesting',
      selectNotIngestReason: 'Reason for not ingesting',
      notIngestReasonOptions: {
        ingesting: 'Already ingesting',
        check: 'Need to check with my data team',
        other: 'Another reason',
      },
      notIngestReasonOther:
        'If the reason is "Another reason", please provide details',
      notIngestReasonOtherPlaceholder: 'Details',
      save: 'Save',
      saving: 'Saving…',
      saveFailed: 'Destination could not be saved.',
      saved: 'Destination saved.',
      cacheCleared: 'Credential cache cleared.',
      contentSyncQueued: 'Content library sync queued.',
      requiredField: 'This field is required.',
      invalidField: 'Enter a valid value.',
      actionFailed: 'Destination action could not be completed.',
      clearCache: 'Clear Cache',
      syncContentLibrary: 'Sync content library',
      formConfiguration: 'Configuration',
      formConfigurationDescription:
        'This destination has {count} configuration fields across {sections} sections.',
      noFormConfiguration: 'This destination has no configuration fields.',
      capabilities: 'Available actions',
      enabled: 'Available',
      disabled: 'Unavailable',
      noCapabilities: 'No actions are available for this destination.',
    },
  },
  [SupportedLocales.ES]: {
    title: 'Destinos',
    subtitle: 'Administra destinos para tus audiencias.',
    table: {
      ariaLabel: 'Tabla de destinos',
      loadingAriaLabel: 'Cargando destinos',
      search: 'Buscar',
      type: 'Tipo',
      name: 'Nombre',
      activeAudiences: 'Audiencias activas',
      syncFrequency: 'Frecuencia de sincronización',
      status: 'Estado',
      lastUpdated: 'Última actualización',
      createdAt: 'Fecha de creación',
      options: 'Opciones',
      active: 'Activo',
      emptyValue: '-',
      empty: 'No hay destinos.',
      emptyCaption: 'Parece que aún no has agregado un destino.',
      emptySearch: 'No hay destinos que coincidan con tu búsqueda.',
      connect: 'Conectar destino',
      deleteAriaLabel: (name: string) => `Eliminar ${name}`,
    },
    reauthenticate: {
      action: 'Volver a autenticar',
      tooltip:
        'La autenticación del destino ha expirado. Vuelve a autenticarte para reiniciar tu sincronización.',
    },
    delete: {
      title: 'Eliminar destino',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      withActiveExports:
        'Este destino tiene exportaciones activas. Eliminarlo deshabilitará las exportaciones futuras, pero conservará el registro de las exportaciones anteriores a este destino.',
      confirmQuestion: '¿Seguro que quieres eliminar este destino?',
      success: 'El destino se ha eliminado.',
      failed: 'No se pudo eliminar el destino',
    },
    oauth: {
      connectionError: 'No se puede conectar a tu destino en este momento',
    },
    edit: {
      requiredField: 'Este campo es obligatorio.',
      invalidField: 'Introduce un valor válido.',
      loadingAriaLabel: 'Cargando destino',
      invalidId: 'No se pudo encontrar este destino.',
      loadFailed: 'No se pudo cargar el destino.',
      platformLabel: 'Plataforma',
      headerCaption: 'Modifica tu plataforma de destino.',
      breadcrumbLabel: 'Ruta de navegación',
      replaceSavedCredential: (label: string) =>
        `Reemplazar valor guardado de ${label}`,
      destinationDetails: 'Destino',
      name: 'Nombre del destino',
      destinationNamePlaceholder: 'Nombre del destino',
      syncFrequency: 'Frecuencia de sincronización',
      selectSyncFrequency: 'Frecuencia de sincronización',
      syncFrequencyOptions: {
        hourly: 'Cada hora',
        daily: 'Diariamente',
      },
      enabledLabel: 'Habilitado',
      channelConfiguration: 'Configuración del canal',
      selectChannelConfiguration: 'Configuración del canal',
      ingest: 'Ingesta',
      importSource: {
        notIngesting: 'Parece que no estás importando datos.',
        ingesting:
          'Esta es la fuente desde la que estás importado datos desde este destino. Puedes seleccionar una fuente diferente si deseas cambiarla.',
        selectExisting:
          'Si ya estás importando, selecciona la fuente de donde estás obteniendo estos datos.',
      },
      importSourceCaption: {
        withSources: {
          prefix:
            'También puedes agregar una nueva fuente siguiendo este enlace: ',
          suffix: ', si ninguna de las anteriores es la que estás utilizando.',
        },
        withoutSources: {
          prefix:
            'Puedes agregar una fuente para importar este destino siguiendo este enlace: ',
        },
      },
      addImportSource: 'Agregar Fuente de Importación',
      selectImportSource: 'Fuente de Importación',
      notIngestReason: 'O cuéntanos por qué no estás importando',
      selectNotIngestReason: 'Razón por la que no estás importando',
      notIngestReasonOptions: {
        ingesting: 'Ya se está ingiriendo',
        check: 'Necesito consultarlo con mi equipo de datos',
        other: 'Otro motivo',
      },
      notIngestReasonOther:
        'Si la razón es "Otra razón", por favor proporciona detalles',
      notIngestReasonOtherPlaceholder: 'Detalles',
      save: 'Guardar',
      saving: 'Guardando…',
      saveFailed: 'No se pudo guardar el destino.',
      saved: 'Destino guardado.',
      cacheCleared: 'Caché de credenciales borrada.',
      contentSyncQueued:
        'Sincronización de la biblioteca de contenido en cola.',
      actionFailed: 'No se pudo completar la acción del destino.',
      clearCache: 'Borrar caché',
      syncContentLibrary: 'Sincronizar biblioteca de contenido',
      formConfiguration: 'Configuración',
      formConfigurationDescription:
        'Este destino tiene {count} campos de configuración en {sections} secciones.',
      noFormConfiguration: 'Este destino no tiene campos de configuración.',
      capabilities: 'Acciones disponibles',
      enabled: 'Disponible',
      disabled: 'No disponible',
      noCapabilities: 'No hay acciones disponibles para este destino.',
    },
  },
};
